import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { GET_JOBS_FILTERS_QUERY, GET_JOBS_QUERY } from '../../operations';
import React, { useEffect, useState } from 'react';
import { titleize, truncate } from '../../../../utils/helpers';

import { AdminsPageShell } from '../../../../components/AdminsPageShell';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { JobModal } from '../JobModal/JobModal';
import { formatTimestamp } from '../../../../utils/helpers';
import { styles } from './styles';
import { useQuery } from '@apollo/client';

const LoadingState = () => {
  return (
    <Box sx={styles.loadingContainer}>
      <CircularProgress size={40} sx={styles.spinner} />
      <Typography sx={styles.loadingText}>Loading...</Typography>
    </Box>
  );
};

const ErrorState = ({ error, onRetry }) => {
  return (
    <Box sx={styles.errorContainer}>
      <ErrorOutlineIcon sx={styles.errorIcon} />
      <Typography sx={styles.errorText}>Something went wrong</Typography>
      <Typography sx={styles.errorMessage}>{error.message}</Typography>
      <Button variant="contained" color="primary" onClick={onRetry} sx={styles.retryButton}>
        Try Again
      </Button>
    </Box>
  );
};

const Filter = ({ filter, value, setValue, label }) => {
  return (
    <>
      {filter && (
        <FormControl sx={styles.select}>
          <InputLabel>{label}</InputLabel>
          <Select value={value} onChange={(e) => setValue(e.target.value)}>
            <MenuItem value="all">
              <em>All</em>
            </MenuItem>
            {filter.map((key) => (
              <MenuItem key={key} value={key} sx={styles.menuItem}>
                {titleize(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  );
};

const Filters = ({ onApply }) => {
  const [status, setStatus] = useState('all');
  const [step, setStep] = useState('all');
  const [reportName, setReportName] = useState('all');
  const [region, setRegion] = useState('lower48');
  const [areaCode, setAreaCode] = useState('all');
  const [startedBy, setStartedBy] = useState('all');

  const { data, loading, error, refetch } = useQuery(GET_JOBS_FILTERS_QUERY);

  const filters = data?.nexusJobsFilters;

  const handleFilterChange = () => {
    onApply({
      status: status === 'all' ? null : status,
      step: step === 'all' ? null : step,
    });
  };

  return (
    <Box sx={styles.filterContainer}>
      {error && <ErrorState error={error} onRetry={() => refetch()}></ErrorState>}
      {loading && <LoadingState />}

      <Filter
        filter={filters?.startedBy}
        value={startedBy}
        setValue={setStartedBy}
        label="Started By"
      />
      <Filter filter={filters?.regions} value={region} setValue={setRegion} label="Region" />
      <Filter filter={filters?.steps} value={step} setValue={setStep} label="Step" />
      <Filter
        filter={filters?.areaCodes}
        value={areaCode}
        setValue={setAreaCode}
        label="Area Code"
      />
      <Filter
        filter={filters?.reportNames}
        value={reportName}
        setValue={setReportName}
        label="Report Name"
      />
      <Filter filter={filters?.jobStatuses} value={status} setValue={setStatus} label="Status" />

      {filters && (
        <Button variant="contained" color="primary" onClick={handleFilterChange} sx={styles.button}>
          Apply Filters
        </Button>
      )}
    </Box>
  );
};

const ListItem = ({ job, onRowClick }) => {
  return (
    <TableRow key={job.id} sx={styles.tableRow}>
      <TableCell>
        <Link href="#" onClick={() => onRowClick(job.id)} color="#0000EE">
          {truncate(job.id, 7)}
        </Link>
      </TableCell>
      <TableCell>{job.status}</TableCell>
      <TableCell>{job.step}</TableCell>
      <TableCell>{job.region}</TableCell>
      <TableCell>{job.areaCode}</TableCell>
      <TableCell sx={styles.cellNoWrap}>{formatTimestamp(job.createdAt)}</TableCell>
      <TableCell sx={styles.cellNoWrap}>{formatTimestamp(job.updatedAt)}</TableCell>
      <TableCell>{truncate(job.message, 100)}</TableCell>
      <TableCell>
        <Link href="#" onClick={() => onRowClick(job.previousJobId)} color="#0000EE">
          {truncate(job.previousJobId, 7)}
        </Link>
      </TableCell>
    </TableRow>
  );
};

const List = ({ jobs, onRowClick }) => (
  <TableContainer component={Paper} sx={styles.tableContainer}>
    <Table>
      <TableHead sx={styles.tableHeader}>
        <TableRow sx={styles.tableRow}>
          <TableCell>ID</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Step</TableCell>
          <TableCell>Region</TableCell>
          <TableCell>Area Code</TableCell>
          <TableCell>Created At</TableCell>
          <TableCell>Updated At</TableCell>
          <TableCell>Message</TableCell>
          <TableCell>Previous job ID</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs?.map((job) => (
          <ListItem key={job.id} job={job} onRowClick={(id) => onRowClick(id)} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const JobStatusList = () => {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedJobId, setSelectedJobId] = useState(null);

  const { data, loading, error, refetch } = useQuery(GET_JOBS_QUERY, {
    variables: selectedFilters,
  });
  const jobs = data?.nexusJobs || [];

  const handleFilterChange = (newFilters) => {
    setSelectedFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, ...newFilters };

      const params = new URLSearchParams(
        Object.entries(updatedFilters).filter(([, value]) => value !== null && value !== ''),
      );
      window.history.replaceState(null, '', `?${params.toString()}`);

      return updatedFilters;
    });
  };

  useEffect(() => {
    refetch(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    return () => {
      setSelectedFilters({});
      setSelectedJobId(null);
    };
  }, []);

  return (
    <AdminsPageShell title="Nexus - Job Status">
      <Filters onApply={handleFilterChange} />
      {error && <ErrorState error={error} onRetry={() => refetch(selectedFilters)}></ErrorState>}
      {jobs && <List jobs={jobs} onRowClick={(jobId) => setSelectedJobId(jobId)} />}
      {loading && <LoadingState />}
      {selectedJobId && (
        <JobModal
          jobId={selectedJobId}
          open={() => Boolean(selectedJobId)}
          onClose={() => setSelectedJobId(null)}
        ></JobModal>
      )}
    </AdminsPageShell>
  );
};
